import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import b64toBlob from 'b64-to-blob'
import {
  RECEIVE_CHECKRUNS,
  RECEIVE_CHECKRUN_METADATA,
  RECEIVE_CHECKRUN_FAILURE,
  SAVING_CHECKRUN,
  SAVING_CHECKRUN_FAILURE,
  UPDATING_CHECKRUN,
  UPDATING_CHECKRUN_FAILURE,
  DELETE_CHECKRUN,
  DELETING_CHECKRUN,
  DELETING_CHECKRUN_FAILURE,
  RECEIVE_QUOTE,
  RECEIVE_QUOTE_FAILURE,
  RECEIVE_CHECK_PDF,
  RECEIVING_CHECK_PDF,
  RECEIVE_CHECK_PDF_FAILURE,
  CLEAR_CHECKRUN_STATE,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'

const checkrunSchema = new schema.Entity('checkruns')
const checkrunListSchema = new schema.Array(checkrunSchema)

const state = {
  data: null,
  quote: null,
  count: null,
  shown: null,
  next: null,
  previous: null,
  saving: { status: false, err: null },
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  checkrunPdf: null,
  checkrunPdfBlob: null,
  checkrunPdfUrl: null,
  receivingCheckrunPdf: false,
  receivingCheckrunPdfFailure: null,
  deleting: false
}

const actions = {
  fetchCheckrunQuote (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('runs/quote', [{ ap__id: query }, 'in'], instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r || !r.length) return this.commit(RECEIVE_QUOTE_FAILURE, 'No response')
        this.commit(RECEIVE_QUOTE, r)
        return r
      })
      .catch(e => {
        return this.commit(RECEIVE_QUOTE_FAILURE, handleXHRError(e))
      })
  },
  fetchCheckrunsByPage (store, page) {
    const { previous, next } = store.state
    const pageUrl = previous || next
    const headers = getHeaders(this.getters.getToken)
    if (pageUrl) {
      let url = new URL(pageUrl)
      url.searchParams.set('page', `${page}`)
      return Vue.http.get(url.href, { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          if (r) {
            const { results, next, previous, count } = r
            const { entities, result } = normalize(results, checkrunListSchema)
            if (!result) return this.commit(RECEIVE_CHECKRUNS, r)
            this.commit(RECEIVE_CHECKRUNS, entities.checkruns)
            this.commit(RECEIVE_CHECKRUN_METADATA, { next, previous, count, result: result.length })
            return entities
          }
        })
        .catch(e => {
          return this.commit(RECEIVE_CHECKRUN_FAILURE, handleXHRError(e))
        })
    }
  },
  saveCheckrun (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_CHECKRUN, true)
    this.commit(SAVING_CHECKRUN_FAILURE, null)
    return Vue.http.post(buildUrl('runs', null, instanceUrl), payload, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(SAVING_CHECKRUN, false)
        if (r) {
          const { entities, result } = normalize(r, checkrunSchema)
          if (!result) return this.commit(RECEIVE_CHECKRUNS, r)
          this.commit(RECEIVE_CHECKRUNS, entities.checkruns)
          this.commit(RECEIVE_CHECKRUN_METADATA)
          return r
        }
      })
      .catch(e => {
        this.commit(SAVING_CHECKRUN, false)
        this.commit(SAVING_CHECKRUN_FAILURE, handleXHRError(e))
      })
  },
  updateCheckrun (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_CHECKRUN, id)
    this.commit(UPDATING_CHECKRUN_FAILURE, { id: '', err: '' })
    return Vue.http.patch(buildUrl(`runs/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_CHECKRUN, null)
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, checkrunSchema)
          if (!result) return this.commit(RECEIVE_CHECKRUNS, r.body)
          this.commit(RECEIVE_CHECKRUNS, entities.checkruns)
          return r.body
        } else {
          return this.commit(RECEIVE_CHECKRUN_FAILURE, r ? r.statusText : 'Failed to update checkrun')
        }
      })
      .catch(e => {
        this.commit(UPDATING_CHECKRUN, null)
        this.commit(UPDATING_CHECKRUN_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  async buildCheckrunPdf (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CHECK_PDF, { status: true })
    try {
      const resp = await Vue.http.get(buildUrl('reports/checks', payload, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (resp.hasOwnProperty('data') && resp.hasOwnProperty('is_base64')) {
        this.commit(RECEIVING_CHECK_PDF, {status: false})
        this.commit(RECEIVE_CHECK_PDF, resp)
        return resp
      } else {
        throw new Error('No PDF data received')
      }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_CHECK_PDF_FAILURE, { err: e.code, msg: 'No PDF data received' })
      return e
    }
  },
  async buildPrintCopyrunPdf (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CHECK_PDF, { status: true })
    try {
      const resp = await Vue.http.get(buildUrl('reports/printcopy', payload, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (resp.hasOwnProperty('data') && resp.hasOwnProperty('is_base64')) {
        this.commit(RECEIVING_CHECK_PDF, {status: false})
        this.commit(RECEIVE_CHECK_PDF, resp)
        return resp
      } else {
        throw new Error('No PDF data received')
      }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_CHECK_PDF_FAILURE, { err: e.code, msg: 'No PDF data received' })
      return e
    }
  },
  clearCheckrunState () {
    return this.commit(CLEAR_CHECKRUN_STATE)
  },
  fetchPaymentrunQuote (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('runs/quote_payments', [{ ap__id: query }, 'in'], instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r || !r.length) return this.commit(RECEIVE_QUOTE_FAILURE, 'No response')
        this.commit(RECEIVE_QUOTE, r)
        return r
      })
      .catch(e => {
        return this.commit(RECEIVE_QUOTE_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getCheckruns: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getCheckrunMetadata: (state) => {
    const { shown, count, previous, next } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, previous, next }
  },
  getCheckrunById: (state) => (id) => {
    return state.data[id]
  },
  getCheckrunFetchStatus: (state) => {
    return state.receivingData
  },
  getCheckrunSaveStatus: (state) => {
    return state.saving
  },
  getCheckrunUpdateStatus: (state) => {
    return state.updating
  },
  getCheckrunQuote: (state) => {
    return state.quote
  },
  getCheckrunPdf: (state) => {
    return state.checkrunPdf
  },
  getCheckrunPdfUrl: (state) => {
    return state.checkrunPdfUrl
  }
}

const mutations = {
  [RECEIVE_CHECKRUNS] (state, payload) {
    if (!payload || !Object.keys(payload).length) return state.data
    if (state.data && Object.keys(state.data).length) {
      state.data = Object.assign({}, state.data, payload)
    } else {
      state.data = payload
    }
  },
  [RECEIVE_CHECKRUN_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_CHECKRUN] (state, payload) {
    state.saving = { status: payload, err: null }
  },
  [SAVING_CHECKRUN_FAILURE] (state, payload) {
    state.saving = { status: false, err: payload }
  },
  [UPDATING_CHECKRUN] (state, payload) {
    state.updating = payload
  },
  [UPDATING_CHECKRUN_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [RECEIVE_QUOTE] (state, payload) {
    state.quote = payload
  },
  [RECEIVE_QUOTE_FAILURE] (state, payload) {
    const { err } = payload
    state.receiveQuoteFailure = { err }
  },
  [DELETE_CHECKRUN] (state, payload) {
    let tempData = Object.assign({}, state.data)
    for (let t in tempData) {
      if (!tempData.hasOwnProperty(t)) continue
      if (tempData[t].id === payload) {
        delete tempData[t]
        state.count = state.count - 1
      }
    }
    state.data = Object.assign({}, tempData)
  },
  [DELETING_CHECKRUN] (state, payload) {
    state.deleting = payload
  },
  [DELETING_CHECKRUN_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { status: false, id, err }
  },
  [RECEIVE_CHECK_PDF] (state, payload) {
    state.checkrunPdf = payload.data
    state.checkrunPdfBlob = b64toBlob(payload.data, 'application/pdf')
    state.checkrunPdfUrl = URL.createObjectURL(state.checkrunPdfBlob)
  },
  [RECEIVING_CHECK_PDF] (state, payload) {
    state.receivingCheckrunPdf = payload
  },
  [RECEIVE_CHECK_PDF_FAILURE] (state, payload) {
    state.receivingCheckrunPdf = false
    state.receivingCheckrunPdfFailure = payload
  },
  [RECEIVE_CHECKRUN_METADATA] (state, payload) {
    if (payload) {
      const { count, next, previous, result } = payload
      if (count) state.count = count
      if (result) state.shown = result
      if (next) state.next = next
      if (previous) state.previous = previous
    } else if (state.data) {
      state.count = Object.keys(state.data).length
    }
  },
  [CLEAR_CHECKRUN_STATE] (state) {
    state.data = null
    state.saving = { status: false, err: null }
    state.receivingData = false
    state.receiveDataFailure = false
    state.updating = null
    state.updateFailure = null
  },
  [CLEAR_STATE] (state) {
    state.data = null
    state.quote = null
    state.count = null
    state.shown = null
    state.next = null
    state.previous = null
    state.saving = { status: false, err: null }
    state.receivingData = false
    state.receiveDataFailure = false
    state.updating = null
    state.updateFailure = null
    state.checkrunPdf = null
    state.checkrunPdfBlob = null
    state.checkrunPdfUrl = null
    state.receivingCheckrunPdf = false
    state.receivingCheckrunPdfFailure = null
    state.deleting = false
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/checkrun.js